import { ToggleButtonGroup } from '@mui/material';
import { useCallback } from 'react';
import { GroupByAttribute } from 'services/data.service';
import { useIntl } from 'react-intl';
import { useAppStore } from 'hooks/state/useAppStore';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { useLocation } from 'react-router-dom';
import { trackMatomoEvent } from 'services/matomo.service';
import { getTrackingPage, MATOMO_ACTIONS } from 'utils/matomoTracking';

const CHART_TOGGLE_TRACKING_NAMES: Record<NonNullable<GroupByAttribute>, string> = {
  chnCountry: 'Country',
  chnMarketplace: 'Marketplace',
  prdBrand: 'Brand',
};

function getChartToggleTrackingName(groupByAttribute: GroupByAttribute): string {
  if (groupByAttribute == null) {
    return 'No Grouping';
  }

  return CHART_TOGGLE_TRACKING_NAMES[groupByAttribute] ?? groupByAttribute;
}

interface GroupByToggleGroupProps {
  trackEvents?: boolean;
}

export function GroupByToggleGroup({ trackEvents = false }: GroupByToggleGroupProps) {
  const intl = useIntl();
  const location = useLocation();

  const { setGroupByAttribute, groupByAttribute } = useAppStore();

  const handleChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: GroupByAttribute) => {
      const nextValue = value ?? null;
      const currentValue = groupByAttribute ?? null;

      if (nextValue === currentValue) {
        return;
      }

      setGroupByAttribute(nextValue);

      if (!trackEvents) {
        return;
      }

      trackMatomoEvent({
        category: getTrackingPage(location.pathname),
        action: MATOMO_ACTIONS.CHART_TOGGLE,
        name: getChartToggleTrackingName(nextValue),
      });
    },
    [groupByAttribute, location.pathname, setGroupByAttribute, trackEvents]
  );

  const options = [
    {
      label: intl.formatMessage({
        id: `groupby.attribute.none`,
        defaultMessage: 'No Grouping',
      }),
      value: '',
    },
    {
      label: intl.formatMessage({
        id: `groupby.attribute.marketplace`,
        defaultMessage: 'Marketplace',
      }),
      value: 'chnMarketplace',
    },
    {
      label: intl.formatMessage({
        id: `groupby.attribute.prdBrnName`,
        defaultMessage: 'Brand',
      }),
      value: 'prdBrand',
    },
    {
      label: intl.formatMessage({
        id: `groupby.attribute.chnCountry`,
        defaultMessage: 'Country',
      }),
      value: 'chnCountry',
    },
  ];

  return (
    <ToggleButtonGroup
      className="ml-auto mb-auto mt-auto"
      exclusive
      value={groupByAttribute ?? ''}
      onChange={handleChange}
    >
      {options.map(option => (
        <StyledToggleButton
          key={option.value || 'none'}
          size="small"
          className="whitespace-nowrap text-xs"
          value={option.value}
          sx={{ color: '#1A1A1A', fontSize: '12px' }}
        >
          {option.label}
        </StyledToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
